import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { horizontalVideos, verticalVideos, thumbnails } from '../../../data/projects';
import './Portfolio.css';

/* ── Video Modal ── */
function VideoModal({ video, title, onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        className="modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-inner"
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94 }}
          transition={{ duration: 0.3 }}
          onClick={e => e.stopPropagation()}
        >
          <button className="modal-close" onClick={onClose}>✕ FECHAR</button>
          <div className="modal-video-wrap">
            <iframe
              src={`${video}?autoplay=1&rel=0&modestbranding=1`}
              title={title}
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Horizontal Video Card ── */
function HorizontalCard({ item, index, onPlay }) {
  return (
    <motion.div
      className="hcard"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: index * 0.1 }}
      onClick={() => item.video && onPlay(item)}
    >
      <div
        className="hcard-thumb"
        style={item.thumb ? { backgroundImage: `url(${item.thumb})` } : {}}
      >
        {!item.thumb && <div className="hcard-gradient" />}
        <div className="hcard-overlay" />
        <div className="hcard-play">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <polygon points="6,3 21,12 6,21" />
          </svg>
        </div>
        <span className="hcard-index">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <div className="hcard-info">
        <div>
          <h3 className="hcard-title">{item.title}</h3>
          {item.description && <p className="hcard-desc">{item.description}</p>}
        </div>
        <div className="hcard-tags">
          {item.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Vertical Video Card ── */
function VerticalCard({ item, index, onPlay }) {
  return (
    <motion.div
      className="vcard"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: index * 0.1 }}
      onClick={() => item.video && onPlay(item)}
    >
      <div
        className="vcard-thumb"
        style={item.thumb ? { backgroundImage: `url(${item.thumb})` } : {}}
      >
        {!item.thumb && <div className="vcard-gradient" />}
        <div className="vcard-overlay" />
        <div className="vcard-play">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <polygon points="6,3 21,12 6,21" />
          </svg>
        </div>
        <span className="vcard-index">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <div className="vcard-info">
        <h3 className="vcard-title">{item.title}</h3>
        <div className="hcard-tags">
          {item.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Thumbnail Card ── */
function ThumbnailCard({ item, index }) {
  const [zoomed, setZoomed] = useState(false);

  return (
    <>
      <motion.div
        className="tcard"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.55, delay: index * 0.08 }}
        onClick={() => setZoomed(true)}
      >
        <div
          className="tcard-thumb"
          style={item.thumb ? { backgroundImage: `url(${item.thumb})` } : {}}
        >
          {!item.thumb && <div className="tcard-gradient" />}
          <div className="tcard-overlay" />
          <div className="tcard-zoom">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              <line x1="11" y1="8" x2="11" y2="14"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </div>
          <span className="tcard-index">{String(index + 1).padStart(2, '0')}</span>
        </div>
        <div className="tcard-info">
          <h3 className="tcard-title">{item.title}</h3>
          <div className="hcard-tags">
            {item.tags.map(tag => <span key={tag} className="tag">{tag}</span>)}
          </div>
        </div>
      </motion.div>

      {/* Lightbox */}
      <AnimatePresence>
        {zoomed && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoomed(false)}
          >
            <motion.div
              className="lightbox-inner"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              onClick={e => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setZoomed(false)}>✕ FECHAR</button>
              <img src={item.thumb} alt={item.title} className="lightbox-img" />
              <div className="lightbox-caption">
                <span className="hcard-title">{item.title}</span>
                {item.description && <span className="hcard-desc">{item.description}</span>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Section Header ── */
function SectionHeader({ num, title, outline }) {
  return (
    <div className="port-header">
      <p className="section-num">{num}</p>
      <h2 className="section-title">
        {title}<br />
        <span className="outline">{outline}</span>
      </h2>
    </div>
  );
}

/* ── Main Component ── */
export default function Portfolio() {
  const [activeModal, setActiveModal] = useState(null);

  return (
    <>
      {/* VÍDEOS HORIZONTAIS */}
      <section className="port-section" id="work">
        <SectionHeader
          num="01 — Edição de Vídeo"
          title="VÍDEOS"
          outline="HORIZONTAIS"
        />
        <div className="hgrid">
          {horizontalVideos.map((item, i) => (
            <HorizontalCard
              key={item.id}
              item={item}
              index={i}
              onPlay={setActiveModal}
            />
          ))}
        </div>
      </section>

      {/* VÍDEOS VERTICAIS */}
      <section className="port-section port-section--dark" id="vertical">
        <SectionHeader
          num="02 — Shorts & Reels"
          title="VÍDEOS"
          outline="VERTICAIS"
        />
        <div className="vgrid">
          {verticalVideos.map((item, i) => (
            <VerticalCard
              key={item.id}
              item={item}
              index={i}
              onPlay={setActiveModal}
            />
          ))}
        </div>
      </section>

      {/* THUMBNAILS */}
      <section className="port-section" id="thumbnails">
        <SectionHeader
          num="03 — Thumbnail Design"
          title="THUMB"
          outline="NAILS"
        />
        <div className="tgrid">
          {thumbnails.map((item, i) => (
            <ThumbnailCard key={item.id} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* VIDEO MODAL */}
      {activeModal && (
        <VideoModal
          video={activeModal.video}
          title={activeModal.title}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}
